import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import { InsertDriveFile, Close } from '@mui/icons-material';
import apiClient from '../../api';

function isImageType(contentType, filename) {
    const mime = String(contentType || '').toLowerCase();
    if (mime.startsWith('image/') && !mime.includes('heic') && !mime.includes('heif')) return true;
    const ext = String(filename || '').split('.').pop().toLowerCase();
    return ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext);
}

/**
 * Loads a short-lived S3 view URL for a stored receipt and shows a thumbnail or file chip.
 */
export default function ReceiptAttachmentGallery({
    attachments = [],
    ownerId,
    kind = 'checklist', // 'checklist' | 'draft'
    onRemove,
    removingId,
}) {
    const [urls, setUrls] = useState({});
    const [loadingId, setLoadingId] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const list = Array.isArray(attachments) ? attachments : [];
        const load = async () => {
            if (!ownerId) return;
            const next = {};
            for (const att of list) {
                const id = att?._id || att?.id;
                if (!id) continue;
                const path = kind === 'draft'
                    ? `/forms/weekly-checklist/drafts/${ownerId}/receipts/${id}/url`
                    : `/forms/weekly-checklist/${ownerId}/receipts/${id}/url`;
                try {
                    const { data } = await apiClient.get(path);
                    if (cancelled) return;
                    next[String(id)] = data?.data || {};
                } catch (_) {
                    next[String(id)] = null;
                }
            }
            if (!cancelled) setUrls(next);
        };
        load();
        return () => { cancelled = true; };
    }, [attachments, ownerId, kind]);

    const list = Array.isArray(attachments) ? attachments : [];
    if (list.length === 0) return null;

    return (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
            {list.map((att) => {
                const id = String(att._id || att.id || att.key);
                const meta = urls[id] || {};
                const name = att.originalFilename || meta.originalFilename || 'Receipt';
                const contentType = att.contentType || meta.contentType;
                const href = meta.downloadUrl;
                const showImg = href && isImageType(contentType, name);
                return (
                    <Box
                        key={id}
                        sx={{
                            position: 'relative',
                            width: 96,
                            height: 96,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            overflow: 'hidden',
                            bgcolor: 'grey.50',
                        }}
                    >
                        {showImg ? (
                            <Box
                                component="a"
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ display: 'block', width: '100%', height: '100%' }}
                            >
                                <Box
                                    component="img"
                                    src={href}
                                    alt={name}
                                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                            </Box>
                        ) : (
                            <Button
                                href={href || undefined}
                                target="_blank"
                                rel="noopener noreferrer"
                                disabled={!href}
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    flexDirection: 'column',
                                    textTransform: 'none',
                                    px: 0.5,
                                }}
                            >
                                {href ? <InsertDriveFile fontSize="small" /> : <CircularProgress size={16} />}
                                <Typography variant="caption" noWrap sx={{ maxWidth: 88, mt: 0.5 }}>
                                    {name}
                                </Typography>
                            </Button>
                        )}
                        {onRemove && (
                            <IconButton
                                size="small"
                                aria-label={`Remove ${name}`}
                                onClick={() => {
                                    setLoadingId(id);
                                    Promise.resolve(onRemove(att)).finally(() => setLoadingId(null));
                                }}
                                disabled={removingId === id || loadingId === id}
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    right: 0,
                                    bgcolor: 'rgba(255,255,255,0.9)',
                                    '&:hover': { bgcolor: 'rgba(255,255,255,1)' },
                                }}
                            >
                                {removingId === id ? <CircularProgress size={14} /> : <Close fontSize="small" />}
                            </IconButton>
                        )}
                    </Box>
                );
            })}
        </Stack>
    );
}
